import { useMemo, useState } from 'react';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { SupporterVoicesVideos } from '#src/app/pages/petitionDetails/pageContext';
import type { SupporterVideosList } from '#src/app/pages/petitionDetails/shared/types';
import { useCanPlayWebm } from '#src/app/shared/hooks/petitionActions';

import { useCampaignActionsEnabled } from '../../useCampaignActionsEnabled';

type Result = ModelHookResult<
	{
		supporterVideos: SupporterVideosList | [];
		currentlyPlaying: string;
		showCreateVideo: boolean;
	},
	{
		setCurrentlyPlaying: (videoUrl: string) => void;
		removeVideoFromState: (videoId?: string) => void;
	}
>;

export function useSupporterVideos({
	supporterVoicesVideos: { videos },
}: {
	supporterVoicesVideos: SupporterVoicesVideos;
}): Result {
	const [currentlyPlaying, setCurrentlyPlaying] = useState('');

	const {
		data: {
			petition: { canCreateVideo },
		},
	} = usePetitionDetailsPageData();

	const canPlayWebm = useCanPlayWebm();
	const campaignActionsEnabled = useCampaignActionsEnabled();

	const displayVideos = useMemo(() => {
		if (canPlayWebm) return videos;
		// Right now GQL returns all transcoded + unprocessed videos. We want to display transcoded videos for browsers (e.g., iOS) that don't support .webm.
		// Mux URLs are always included because MuxPlayer handles format selection internally.
		return videos.filter(
			({ videoUrl }) => videoUrl.endsWith('.mp4') || /^https?:\/\/stream\.mux\.com\//.test(videoUrl),
		);
	}, [canPlayWebm, videos]);

	const [supporterVideos, setSupporterVideos] = useState<SupporterVideosList>(displayVideos);

	const removeVideoFromState = (videoId?: string) => {
		const remainingVideos = supporterVideos.filter((v) => v.id !== videoId);
		setSupporterVideos(remainingVideos);
	};

	return {
		data: {
			supporterVideos,
			currentlyPlaying,
			showCreateVideo: campaignActionsEnabled && canCreateVideo,
		},
		actions: {
			setCurrentlyPlaying,
			removeVideoFromState,
		},
	};
}
